import React, { useState, useEffect } from "react";
import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import StarFilled from "./icons/StarFilled";
import StarEmpty from "./icons/StarEmpty";
import StarHalf from "./icons/StarHalf";
import "./PluginCard.scss";

const PluginCard = ({
	slug,
	Name,
	Description,
	Image,
	ActiveInstallations,
	LastUpdated,
	Compatibility,
	Rating,
	NumberOfRatings,
	AuthorUrl,
	PluginUrl,
	DownloadLink,
}) => {
	// Possible statuses: 'not_installed', 'installed', 'activated', 'installing', 'activating', 'error'
	const [pluginStatus, setPluginStatus] = useState("checking");
	const [pluginFile, setPluginFile] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");

	// Check plugin status on component mount
	useEffect(() => {
		checkPluginStatus();
	}, [slug]);

	const checkPluginStatus = async () => {
		setPluginStatus("checking");
		setErrorMessage("");

		try {
			const response = await apiFetch({
				path: `/plac/v1/plugins/status`,
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					slug: slug,
				},
			});

			if (response.status) {
				setPluginStatus(response.status);
				if (response.plugin_file) {
					setPluginFile(response.plugin_file);
				}
			} else {
				setPluginStatus("not_installed");
			}
		} catch (error) {
			console.error("Error checking plugin status:", error);
			setPluginStatus("not_installed");
		}
	};

	const renderStars = (rating) => {
		const fullStars = Math.floor(rating);
		const hasHalfStar = rating % 1 !== 0;
		const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
		return (
			<>
				{[...Array(fullStars)].map((_, i) => (
					<StarFilled key={`full-${i}`} />
				))}
				{hasHalfStar && <StarHalf key="half" />}
				{[...Array(emptyStars)].map((_, i) => (
					<StarEmpty key={`empty-${i}`} />
				))}
			</>
		);
	};

	const installPlugin = async () => {
		setPluginStatus("installing");
		setErrorMessage("");
		try {
			// Configure apiFetch to catch and handle non-JSON responses
			apiFetch.use(
				apiFetch.createRootURLMiddleware(window.placAdminData.rest_url),
			);

			// Add custom middleware to handle potential non-JSON responses
			apiFetch.use(async (options, next) => {
				try {
					return await next(options);
				} catch (error) {
					// Check if this is a JSON parsing error
					if (
						error.message.includes("invalid json response") &&
						error.response
					) {
						try {
							// Try to extract JSON from potential HTML output
							const responseText = await error.response.text();
							const jsonMatch = responseText.match(/\{.*\}/s);
							if (jsonMatch) {
								return JSON.parse(jsonMatch[0]);
							}
						} catch (extractError) {
							console.error(
								"Failed to extract JSON from response:",
								extractError,
							);
						}
					}
					throw error;
				}
			});

			const installResponse = await apiFetch({
				path: "/plac/v1/plugins/install",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					plugin_name: Name,
					download_url: DownloadLink,
				},
			});

			if (installResponse.success && installResponse.plugin_file) {
				setPluginFile(installResponse.plugin_file);
				// First show "Installed" status
				setPluginStatus("installation_complete");

				// Then after a brief delay, change to "Activate" status
				setTimeout(() => {
					setPluginStatus("installed");
				}, 1500); // 1.5 seconds delay
			} else {
				throw new Error(
					installResponse.message || "Plugin installation failed",
				);
			}
		} catch (error) {
			console.error("Plugin Installation Error:", error);
			setErrorMessage(error.message || "Installation failed");
			setPluginStatus("error");
		}
	};

	const activatePlugin = async () => {
		if (!pluginFile) {
			setErrorMessage("Plugin file not found");
			setPluginStatus("error");
			return;
		}

		setPluginStatus("activating");
		setErrorMessage("");

		try {
			const activateResponse = await apiFetch({
				path: "/plac/v1/plugins/activate",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					plugin_file: pluginFile,
				},
			});

			if (activateResponse.success) {
				setPluginStatus("activated");
			} else {
				throw new Error(
					activateResponse.message || "Activation failed",
				);
			}
		} catch (error) {
			console.error("Plugin Activation Error:", error);
			setErrorMessage(error.message || "Activation failed");
			setPluginStatus("error");
		}
	};

	const getButtonLabel = () => {
		switch (pluginStatus) {
			case "checking":
				return __("Checking...", "access-control");
			case "not_installed":
				return __("Install Now", "access-control");
			case "installed":
				return __("Activate", "access-control");
			case "installing":
				return __("Installing...", "access-control");
			case "installation_complete": // New state
				return __("Installed", "access-control");
			case "activating":
				return __("Activating...", "access-control");
			case "activated":
				return __("Activated", "access-control");
			case "error":
				return __("Try Again", "access-control");
			default:
				return __("Install Now", "access-control");
		}
	};

	const handleButtonClick = () => {
		switch (pluginStatus) {
			case "not_installed":
				installPlugin();
				break;
			case "installed":
				activatePlugin();
				break;
			case "error":
				checkPluginStatus();
				break;
			default:
				break;
		}
	};

	const isButtonDisabled = ["checking", "installing", "activating","installation_complete"].includes(
		pluginStatus,
	);

	return (
		<div className="plugin-card">
			<div className="top-section">
				<div className="pl-icon">
					<img
						src={Image}
						alt="Plugin Icon"
						width="128"
						height="128"
					/>
				</div>
				<div className="plugin-info">
					<div className="info-container">
						<h2 className="plugin-title">
							{__(Name, "access-control")}
						</h2>
						<p className="plugin-description">
							{__(Description, "access-control")}
						</p>
						<p className="plugin-author">
							{__("By", "access-control")}{" "}
							<a href={AuthorUrl} className="author-name">
								{__("Programmelab", "access-control")}
							</a>
						</p>
						{errorMessage && (
							<p className="plugin-error-message">
								{errorMessage}
							</p>
						)}
					</div>
				</div>
				<div className="ac-buttons">
					<button
						onClick={handleButtonClick}
						className={`install-button ${pluginStatus}`}
						disabled={isButtonDisabled}
					>
						{getButtonLabel()}
					</button>
					<a
						href={PluginUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="more-details"
					>
						{__("More Details", "access-control")}
					</a>
				</div>
			</div>

			<div className="bottom-section">
				<div className="rating-section">
					<div className="stars-container">
						<div className="stars">{renderStars(Rating)}</div>
						<span className="rating-count">
							({__(NumberOfRatings, "access-control")})
						</span>
					</div>
					<p className="installation-count">
						{__(ActiveInstallations, "access-control")}
					</p>
				</div>
				<div className="update-info">
					<div className="last-updated">
						<span className="update-label">
							{__("Last Updated", "access-control")}:
						</span>
						<span className="update-time">
							{__(LastUpdated, "access-control")}
						</span>
					</div>
					<p className="compatibility">
						{__(Compatibility, "access-control")}
					</p>
				</div>
			</div>
		</div>
	);
};

export default PluginCard;
